/* Copyright (c) 2020-present CloudIO Inc */
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './AngularGauge';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { page as SamplePage } from '../../help/sample_pages/AngularGaugePage';
const AngularGaugeManager: ComponentManagerFn<'AngularGauge'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AngularGauge'>(platform, 'AngularGauge'),
    type: 'AngularGauge',
    name: 'Angular Gauge',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'It is a column through which you can get the data from DataSource.',
      },
      keyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Key Attribute',
        type: 'ComboAttributeChooser',
      },
      labelAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        type: 'ComboAttributeChooser',
      },
      orderByAttribute: {
        label: 'Order By Attribute',
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        type: 'ComboAttributeChooser',
      },
      caption: {
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a title for the Chart.',
      },
      subCaption: {
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a sub title for the chart.',
      },
      upperLimit: {
        label: 'Upper Limit',
        type: 'expression',
        returnType: async () => 'number',
        category: 'General',
        helpText:
          'Specify the upper limit (or the maximum value) of the gauge scale.',
      },
      lowerLimit: {
        label: 'Lower Limit',
        type: 'expression',
        returnType: async () => 'number',
        category: 'General',
        helpText:
          'Specify the lower limit (or the minimum value) of the gauge scale.',
      },
      gaugeFillColor: {
        label: 'Gauge Fill Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code of the color of the Gauge Fill.',
      },
      dialFillColor: {
        label: 'Dial Fill Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code of the color of the Needle.',
      },
      bgColor: {
        label: 'BackGround Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code of the background color.',
      },
      majorTMColor: {
        label: 'Major TM Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code of the color of the major tick marks.',
      },
      minorTMColor: {
        label: 'Minor TM Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code of the color of the minor tick marks.',
      },
      dialValue: {
        label: 'Dial Value',
        type: 'expression',
        returnType: async () => 'number',
        helpText:
          'These attributes let you customize dial value that will be shown on the chart.',
      },
      forceDecimals: {
        label: 'Force Decimals',
        type: 'boolean',
        category: 'General',
      },
      showTickMarks: {
        label: 'Show Tick Marks',
        type: 'boolean',
        category: 'General',
      },
      showTickValues: {
        label: 'Show Tick Values',
        type: 'boolean',
        category: 'General',
      },
      showValue: {
        label: 'Show Value',
        type: 'boolean',
        category: 'General',
      },
      showBorder: {
        label: 'Show Border',
        type: 'boolean',
        category: 'General',
      },
      placeTicksInside: {
        label: 'Place Ticks Inside',
        type: 'boolean',
        category: 'General',
      },
      placeValuesInside: {
        label: 'Place Values Inside',
        type: 'boolean',
        category: 'General',
      },
      exportEnabled: {
        label: 'Export Enabled',
        type: 'boolean',
        category: 'General',
      },
      exportFileName: {
        label: 'Export File Name',
        type: 'string',
        category: 'General',
        helpText:
          'Enable this to display all the labels in lite color when the chart background color is dark.',
      },
      numberPrefix: {
        label: 'Number Prefix',
        type: 'string',
        category: 'General',
      },
      numberSuffix: {
        label: 'Number Suffix',
        type: 'string',
        category: 'General',
      },
      decimalSeparator: {
        label: 'Decimal Separator',
        type: 'string',
        category: 'General',
        helpText: 'Customize depending on your requirement.',
      },
      gaugeOuterRadius: {
        label: 'Gauge Outer Radius',
        type: 'string',
        category: 'General',
        helpText:
          'Specify the outer radius (in pixels) for the color range scale.',
      },
      gaugeInnerRadius: {
        label: 'Gauge Inner Radius',
        type: 'string',
        category: 'General',
        helpText:
          'Specify the Inner radius (in pixels) for the color range scale.',
      },
      gaugeStartAngle: {
        label: 'Gauge Start Angle',
        type: 'string',
        category: 'General',
        helpText:
          'Set the angle (between -360 and 360) at which the gauge will start drawing.',
      },
      gaugeEndAngle: {
        label: 'Gauge End Angle',
        type: 'string',
        category: 'General',
        helpText:
          'Set the angle (between -360 and 360) at which the gauge will finish drawing.',
      },
      thousandSeparator: {
        label: 'Thousand Separator',
        type: 'string',
        category: 'General',
        helpText: 'Customize depending on your requirement.',
      },
      thousandSeparatorPosition: {
        label: 'Thousand Separator Position',
        type: 'string',
        category: 'General',
        helpText: 'Default Value is 3 from Right to Left.',
      },
      majorTMNumber: {
        label: 'Major TM Number',
        type: 'string',
        category: 'General',
        helpText:
          'Specify the number of major tick marks to be rendered on the gauge scale',
      },
      minorTMNumber: {
        label: 'Minor TM Number',
        type: 'string',
        category: 'General',
        helpText:
          'Specify the number of minor tick marks to be rendered on the gauge scale',
      },
      majorTMHeight: {
        label: 'Major TM Height',
        type: 'string',
        category: 'General',
        helpText: 'Specify the height of the major tick marks.',
      },
      borderThickness: {
        label: 'Dial Border Thickness',
        type: 'number',
        category: 'General',
        max: 10,
      },
      tickValueStep: {
        label: 'Tick Value Step',
        type: 'number',
        category: 'General',
        max: 10,
      },
      decimals: {
        label: 'Decimals',
        type: 'number',
        category: 'General',
      },
      dialValueFontSize: {
        label: 'Dial Value FontSize',
        type: 'number',
        category: 'General',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        max: 800,
        min: 0,
        step: 40,
        category: 'General',
        helpText: 'Provide a number to set height of the chart.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        category: 'General',
        helpText: 'Provide width for the chart.',
      },
      functionType: {
        category: 'Data',
        label: 'Function',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        type: 'select',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      plotToolText: {
        label: 'Plot ToolText',
        type: 'string',
        helpText:
          'You can either specify a constant string as the tooltip text or you can use variable values from the data level by prefixing a $ to the attribute name. Ex: $label : $dataValue',
      },
      darkBackGround: {
        label: 'Dark Background',
        type: 'boolean',
        helpText:
          'Enable this to display all the labels in lite color when the chart background color is dark.',
      },
      showTrendLine: {
        label: 'Show Trend Line',
        type: 'boolean',
        helpText:
          'Enable this option to display trend lines along the arc of the Angular Gauge',
      },
      startValue: {
        label: 'Start Value',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Trend Lines',
        helpText:
          'Provide a start value for the Trend line. if not it will take the value of dial',
      },
      displayValue: {
        label: 'Label text',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Label of the trend line. If the label field is empty, it will take the value of the trend line.',
      },
      valueInside: {
        label: 'Show Value Inside',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show trend label  inside.',
      },
      dashedLine: {
        label: 'Dashed lines',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show the trend lines as dashed line.',
      },
      lineColor: {
        label: 'Line Color',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Color of the line in hex e.g., "#FF0000, #0372AB, #FF5904...".',
      },
      trendValueDistance: {
        label: 'Trend Value Distance',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Specify the distance (in pixels) between a trend line and its corresponding value e.g.. 10 ,20 ...',
      },
      thickness: {
        label: 'Trend Line Thickness',
        type: 'number',
        min: 1,
        max: 20,
        step: 2,
        category: 'Trend Lines',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    isPropertyVisible(prop, itemProps): boolean {
      switch (prop) {
        case 'exportFileName':
          return itemProps.exportEnabled;
        case 'valueAttribute':
        case 'orderByAttribute':
        case 'functionType':
          return !!itemProps.dsalias;
        case 'dashedLine':
        case 'startValue':
        case 'lineColor':
        case 'thickness':
        case 'valueInside':
        case 'displayValue':
        case 'trendValueDistance':
          return !!itemProps.showTrendLine;
        default:
          return true;
      }
    },
    category: 'Fusion Charts',
    icon: { iconName: 'gauge-simple' },
    description: `**Overview**<br/>
The Angular Gauge component renders a circular (or semi-circular) speedometer-style chart used to display a single metric within a predefined range. It is ideal for visualizing **Key Performance Indicators** (KPIs) where the current value needs to be contextualized against minimum and maximum limits.
Use this component to show metrics like "Customer Satisfaction Score," "Server Load," "Revenue Progress," or "Speed."

**1. Data Connection**<br/>
The gauge connects to a Data Store to aggregate or read the value to be displayed.
* **DataSource Alias (dsalias):** Selects the Data Store (Cloud, Local, or ReadOnly) containing the dataset.
* **Value Attribute:** The specific numeric column to analyze.
* **Function (functionType):** Determines how the data is processed if multiple records exist. Options include **Sum**, **Avg**, **Max**, **Min**, and **Count**.
* **Dial Value:** An expression property that allows you to manually override the displayed value (e.g., calculating a custom percentage via formula).

**2. Scale & Limits**<br/>
You must define the boundaries of the gauge to make the needle position meaningful.
* **Lower Limit / Upper Limit:** Sets the minimum (0%) and maximum (100%) values of the scale. (Example: For an NPS Score, set Lower: -100, Upper: 100).
* **Gauge Angles:** Customize the shape of the gauge.
    * **Full Circle:** Start: 0, End: 360.
    * **Half Circle (Speedometer):** Start: 180, End: 0.
    * **Quarter Circle:** Start: 270, End: 360.

**3. Visual Configuration**<br/>
You have extensive control over the look and feel of the gauge elements.
* **Colors:** Customize specific elements using hex codes or expressions, including **Gauge Fill**, **Dial Fill (Needle)**, and **Tick Marks**.
* **Tick Marks:** Configure the density and placement of scale markers using **Major TM Number**, **Minor TM Number**, and **Place Ticks Inside**.
* **Radius:** Adjust **Gauge Outer Radius** and **Gauge Inner Radius** to control the thickness of the colored arc.

**4. Trend Points & Targets**<br/>
You can overlay trend lines to indicate target goals or critical thresholds.
* **Show Trend Line:** Enables the reference line feature.
* **Start Value:** The specific value on the scale where the line should appear (e.g., at "80" for a sales target).
* **Styling:** Customize the **Line Color**, **Thickness**, and whether it is a **Dashed Line**.
* **Label:** Use **Label Text** to annotate the line (e.g., "Target").

**5. Number Formatting**<br/>
Ensure the displayed value is readable and context-appropriate.
* **Prefix / Suffix:** Add symbols like "$" or "%".
* **Decimals:** Control precision with **Decimals** and **Force Decimals** (e.g., to always show "10.00").
* **Separators:** Configure **Thousand Separator** and **Decimal Separator** for localization.

**6. Best Use Cases**<br/>
* **Executive Dashboards:** Displaying a "Revenue vs Target" gauge.
* **IT Monitoring:** Visualizing "CPU Usage" (0-100%) or "System Temperature."
* **Satisfaction Surveys:** Displaying average CSAT or NPS scores.
* **Inventory:** Showing "Stock Level" relative to total warehouse capacity.`,
    helpRenders: [
      {
        title: 'Customer Satisfaction Score',
        description:
          'Basic gauge showing satisfaction score with color-coded ranges for poor, average, and good performance.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 320,
        visibleProps: [
          'caption',
          'subCaption',
          'lowerLimit',
          'upperLimit',
          'showValue',
          'numberSuffix',
          'dialValue',
        ],
        item: {
          children: [],
          itemId: 'angularGauge',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'value',
            functionType: 'Avg',
            caption: {
              type: 'text',
              value: 'Customer Satisfaction Score',
            },
            subCaption: {
              type: 'text',
              value: 'Last Week',
            },
            lowerLimit: {
              type: 'text',
              value: '0',
            },
            upperLimit: {
              type: 'text',
              value: '100',
            },
            showValue: true,
            numberSuffix: '%',
            exportFileName: 'customer_satisfaction',
            showTickMarks: true,
            showTickValues: true,
            showBorder: false,
            placeTicksInside: false,
            placeValuesInside: false,
            forceDecimals: false,
            numberPrefix: '',
            decimals: 2,
            decimalSeparator: '.',
            thousandSeparator: ',',
            thousandSeparatorPosition: '3',
            dialValue: {
              type: 'text',
              value: '70',
            },
            dialValueFontSize: 20,
            chartHeight: 1,
            chartWidth: 1,
            exportEnabled: false,
            doNotPrefetch: false,
            borderThickness: 0,
            tickValueStep: 1,
            gaugeOuterRadius: '',
            gaugeInnerRadius: '',
            gaugeStartAngle: '',
            gaugeEndAngle: '',
            minorTMNumber: '',
            majorTMNumber: '',
            majorTMHeight: '',
            gaugeFillColor: { type: 'text', value: '' },
            bgColor: { type: 'text', value: '' },
            dialFillColor: { type: 'text', value: '' },
            majorTMColor: { type: 'text', value: '' },
            minorTMColor: { type: 'text', value: '' },
            plotToolText: '',
            darkBackGround: false,
            showTrendLine: false,
            startValue: { type: 'text', value: '0' },
            thickness: 2,
            dashedLine: false,
            valueInside: false,
            lineColor: '#000000',
            displayValue: '',
            trendValueDistance: '10',
          },
          type: 'AngularGauge',
        },
      },
      {
        title: 'Performance Dashboard with Trend Line',
        description:
          'Gauge with trend line showing target value, useful for KPI monitoring against goals.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        height: 320,
        visibleProps: [
          'showTrendLine',
          'startValue',
          'displayValue',
          'lineColor',
          'thickness',
          'dialValue',
        ],
        item: {
          children: [],
          itemId: 'angularGauge',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'value',
            functionType: 'Sum',
            caption: {
              type: 'text',
              value: 'Sales Performance',
            },
            subCaption: {
              type: 'text',
              value: 'Q4 2024',
            },
            lowerLimit: {
              type: 'text',
              value: '0',
            },
            upperLimit: {
              type: 'text',
              value: '100',
            },
            showValue: true,
            numberSuffix: '%',
            showTrendLine: true,
            startValue: {
              type: 'text',
              value: '80',
            },
            displayValue: 'Target',
            lineColor: '#E15A26',
            thickness: 3,
            valueInside: false,
            dashedLine: false,
            trendValueDistance: '15',
            exportFileName: 'sales_performance',
            showTickMarks: true,
            showTickValues: true,
            showBorder: false,
            placeTicksInside: false,
            placeValuesInside: false,
            forceDecimals: false,
            numberPrefix: '',
            decimals: 2,
            decimalSeparator: '.',
            thousandSeparator: ',',
            thousandSeparatorPosition: '3',
            dialValue: {
              type: 'text',
              value: '40',
            },
            dialValueFontSize: 20,
            chartHeight: 1,
            chartWidth: 1,
            exportEnabled: false,
            doNotPrefetch: false,
            borderThickness: 0,
            tickValueStep: 1,
            gaugeOuterRadius: '',
            gaugeInnerRadius: '',
            gaugeStartAngle: '',
            gaugeEndAngle: '',
            minorTMNumber: '',
            majorTMNumber: '',
            majorTMHeight: '',
            gaugeFillColor: { type: 'text', value: '' },
            bgColor: { type: 'text', value: '' },
            dialFillColor: { type: 'text', value: '' },
            majorTMColor: { type: 'text', value: '' },
            minorTMColor: { type: 'text', value: '' },
            plotToolText: '',
            darkBackGround: false,
          },
          type: 'AngularGauge',
        },
      },
      {
        title: 'Custom Color Gauge',
        description:
          'Gauge with custom colors for dial, background, and tick marks to match brand identity.',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        height: 320,
        visibleProps: [
          'gaugeFillColor',
          'dialFillColor',
          'bgColor',
          'majorTMColor',
          'minorTMColor',
          'dialValue',
        ],
        item: {
          children: [],
          itemId: 'angularGauge',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'value',
            functionType: 'Max',
            caption: {
              type: 'text',
              value: 'Server Load',
            },
            subCaption: {
              type: 'text',
              value: 'Current Status',
            },
            lowerLimit: {
              type: 'text',
              value: '0',
            },
            upperLimit: {
              type: 'text',
              value: '100',
            },
            showValue: true,
            numberSuffix: '%',
            gaugeFillColor: {
              type: 'text',
              value: '#5D62B5',
            },
            dialFillColor: {
              type: 'text',
              value: '#29C3BE',
            },
            bgColor: {
              type: 'text',
              value: '#F5F5F5',
            },
            majorTMColor: {
              type: 'text',
              value: '#333333',
            },
            minorTMColor: {
              type: 'text',
              value: '#999999',
            },
            exportFileName: 'server_load',
            showTickMarks: true,
            showTickValues: true,
            showBorder: true,
            placeTicksInside: false,
            placeValuesInside: false,
            forceDecimals: false,
            numberPrefix: '',
            decimals: 2,
            decimalSeparator: '.',
            thousandSeparator: ',',
            thousandSeparatorPosition: '3',
            dialValue: {
              type: 'text',
              value: '50',
            },
            dialValueFontSize: 20,
            chartHeight: 1,
            chartWidth: 1,
            exportEnabled: false,
            doNotPrefetch: false,
            borderThickness: 2,
            tickValueStep: 1,
            gaugeOuterRadius: '',
            gaugeInnerRadius: '',
            gaugeStartAngle: '',
            gaugeEndAngle: '',
            minorTMNumber: '',
            majorTMNumber: '',
            majorTMHeight: '',
            plotToolText: '',
            darkBackGround: false,
            showTrendLine: false,
            startValue: { type: 'text', value: '0' },
            thickness: 2,
            dashedLine: false,
            valueInside: false,
            lineColor: '#000000',
            displayValue: '',
            trendValueDistance: '10',
          },
          type: 'AngularGauge',
        },
      },
      {
        title: 'Financial Metric with Prefix',
        description:
          'Display currency values with number prefix and formatting for financial dashboards.',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        height: 320,
        visibleProps: [
          'numberPrefix',
          'decimals',
          'forceDecimals',
          'thousandSeparator',
          'dialValue',
        ],
        item: {
          children: [],
          itemId: 'angularGauge',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'value',
            functionType: 'Sum',
            caption: {
              type: 'text',
              value: 'Revenue',
            },
            subCaption: {
              type: 'text',
              value: 'Monthly Target',
            },
            lowerLimit: {
              type: 'text',
              value: '60',
            },
            upperLimit: {
              type: 'text',
              value: '100',
            },
            showValue: true,
            numberPrefix: '$',
            numberSuffix: 'K',
            decimals: 1,
            forceDecimals: true,
            thousandSeparator: ',',
            thousandSeparatorPosition: '3',
            decimalSeparator: '.',
            exportFileName: 'monthly_revenue',
            showTickMarks: true,
            showTickValues: true,
            showBorder: false,
            placeTicksInside: false,
            placeValuesInside: false,
            dialValue: {
              type: 'text',
              value: '70',
            },
            dialValueFontSize: 24,
            chartHeight: 1,
            chartWidth: 1,
            exportEnabled: false,
            doNotPrefetch: false,
            borderThickness: 0,
            tickValueStep: 1,
            gaugeOuterRadius: '',
            gaugeInnerRadius: '',
            gaugeStartAngle: '',
            gaugeEndAngle: '',
            minorTMNumber: '',
            majorTMNumber: '',
            majorTMHeight: '',
            gaugeFillColor: { type: 'text', value: '' },
            bgColor: { type: 'text', value: '' },
            dialFillColor: { type: 'text', value: '' },
            majorTMColor: { type: 'text', value: '' },
            minorTMColor: { type: 'text', value: '' },
            plotToolText: '$label: $dataValue',
            darkBackGround: false,
            showTrendLine: false,
            startValue: { type: 'text', value: '0' },
            thickness: 2,
            dashedLine: false,
            valueInside: false,
            lineColor: '#000000',
            displayValue: '',
            trendValueDistance: '10',
          },
          type: 'AngularGauge',
        },
      },
      {
        title: 'Custom Gauge Appearance',
        description:
          'Customize gauge dimensions, angles, and tick mark placement for unique visualizations.',
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        height: 320,
        visibleProps: [
          'gaugeStartAngle',
          'gaugeEndAngle',
          'placeTicksInside',
          'placeValuesInside',
          'majorTMNumber',
          'minorTMNumber',
          'dialValue',
        ],
        item: {
          children: [],
          itemId: 'angularGauge',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'value',
            functionType: 'Avg',
            caption: {
              type: 'text',
              value: 'Temperature',
            },
            subCaption: {
              type: 'text',
              value: 'Current Reading',
            },
            lowerLimit: {
              type: 'text',
              value: '0',
            },
            upperLimit: {
              type: 'text',
              value: '100',
            },
            showValue: true,
            numberSuffix: '°C',
            gaugeStartAngle: '180',
            gaugeEndAngle: '0',
            placeTicksInside: true,
            placeValuesInside: true,
            majorTMNumber: '6',
            minorTMNumber: '5',
            majorTMHeight: '10',
            showTickMarks: true,
            showTickValues: true,
            exportFileName: 'temperature_gauge',
            showBorder: false,
            forceDecimals: false,
            numberPrefix: '',
            decimals: 2,
            decimalSeparator: '.',
            thousandSeparator: ',',
            thousandSeparatorPosition: '3',
            dialValue: {
              type: 'text',
              value: '66',
            },
            dialValueFontSize: 20,
            chartHeight: 1,
            chartWidth: 1,
            exportEnabled: false,
            doNotPrefetch: false,
            borderThickness: 0,
            tickValueStep: 1,
            gaugeOuterRadius: '',
            gaugeInnerRadius: '',
            gaugeFillColor: { type: 'text', value: '' },
            bgColor: { type: 'text', value: '' },
            dialFillColor: { type: 'text', value: '' },
            majorTMColor: { type: 'text', value: '' },
            minorTMColor: { type: 'text', value: '' },
            plotToolText: '',
            darkBackGround: false,
            showTrendLine: false,
            startValue: { type: 'text', value: '0' },
            thickness: 2,
            dashedLine: false,
            valueInside: false,
            lineColor: '#000000',
            displayValue: '',
            trendValueDistance: '10',
          },
          type: 'AngularGauge',
        },
      },
    ],
  };
};
export default AngularGaugeManager;
